"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AttachmentClient = void 0;
const BaseClient_1 = require("../BaseClient");
class AttachmentClient extends BaseClient_1.BaseClient {
    constructor(baseUrl, tokenProvider) {
        super(baseUrl, tokenProvider);
        this.baseUrl = baseUrl ? baseUrl : BaseClient_1.baseClientDefaults.baseUrl;
    }
    /**
     * Function for fetching the attachment content.
     * WARNING: It must not be used with large files.
     *
     * @param {AttachmentContentRequest} request - request parameters
     */
    async getContent(request) {
        const response = await this.axiosInstance({
            method: 'GET',
            url: '/api/v1/attachment/get',
            params: {
                entity: request.entity,
                entityId: request.entityId,
                key: request.key,
                version: request.version,
            },
            responseType: 'arraybuffer',
        });
        return {
            content: response.data,
            contentType: response.headers['content-type'],
        };
    }
    /**
     * Returns metadata about the specific attachment. Can be used to check virus scan results.
     * Throws error is attachment does not exist.
     *
     * @param request {AttachmentRequest} - request parameters
     *
     * @returns {AttachmentMetadataResponse} - additional metadata about the specific attachment that contains virus scan results
     */
    async getMetadata(request) {
        const response = await this.axiosInstance({
            method: 'GET',
            url: '/api/v1/attachment/metadata',
            params: {
                entity: request.entity,
                entityId: request.entityId,
                key: request.key,
            },
        });
        return response.data;
    }
    /**
     * Returns a list of attachments for a specific entity.
     * Returns empty array if no attachments exists.
     *
     * @param request {ListAttachmentRequest} - request parameters
     *
     * @returns {AttachmentResponse[]} - list of attachments
     */
    async list(request) {
        const response = await this.axiosInstance({
            method: 'GET',
            url: '/api/v1/attachment/list',
            params: {
                entity: request.entity,
                entityId: request.entityId,
            },
        });
        return response.data;
    }
    async getVersions(request) {
        const response = await this.axiosInstance({
            method: 'GET',
            url: '/api/v1/attachment/versions',
            params: {
                entity: request.entity,
                entityId: request.entityId,
                key: request.key,
                // TODO test undefined params
                limit: request.limit,
                offset: request.offset,
            },
        });
        return response.data;
    }
    /**
     * Uploads attachment
     * @param request - request params
     * @param progressCallback - reports upload progress. NOTE only environments are supported
     */
    async post(request, progressCallback) {
        const response = await this.axiosInstance.request({
            url: '/api/v1/attachment/post',
            method: 'POST',
            headers: {
                'Content-Type': request.contentType ?? 'application/octet-stream',
            },
            params: {
                entity: request.entity,
                entityId: request.entityId,
                key: request.key,
            },
            data: request.content,
            onUploadProgress: (event) => {
                if (progressCallback) {
                    progressCallback(event.loaded);
                }
            },
        });
        return response.data;
    }
    async revertToVersion(request) {
        const response = await this.axiosInstance({
            method: 'POST',
            url: '/api/v1/attachment/revertToVersion',
            data: request,
        });
        return response.data;
    }
    async delete(request) {
        await this.axiosInstance({
            method: 'POST',
            url: '/api/v1/attachment/delete',
            data: request,
        });
    }
    async presignDownload(request) {
        const response = await this.axiosInstance({
            method: 'GET',
            url: '/api/v1/attachment/presign-download',
            params: {
                entity: request.entity,
                entityId: request.entityId,
                key: request.key,
                downloadFileName: request.downloadFileName,
                version: request.version,
                playable: request.playable,
            },
        });
        return {
            ...response.data,
            url: this.baseUrl + response.data.url,
        };
    }
    async beginMultipartUpload(request, retryConfig) {
        const response = await this.axiosInstance({
            method: 'POST',
            url: '/api/v1/attachment/beginMultipartUpload',
            params: request,
            ...retryConfig,
        });
        return response.data;
    }
    async uploadPart(request, progressCallback, retryConfig) {
        const { content, contentType, ...rest } = request;
        const response = await this.axiosInstance({
            method: 'POST',
            url: '/api/v1/attachment/uploadPart',
            headers: {
                'Content-Type': contentType,
            },
            params: rest,
            data: content,
            onUploadProgress: (event) => {
                if (progressCallback) {
                    progressCallback(event.bytes);
                }
            },
            ...retryConfig,
        });
        return response.data;
    }
    async completeMultipartUpload(request, retryConfig) {
        const response = await this.axiosInstance({
            method: 'POST',
            url: '/api/v1/attachment/completeMultipartUpload',
            params: request,
            ...retryConfig,
        });
        return response.data;
    }
}
exports.AttachmentClient = AttachmentClient;
